import { memo, useCallback, useMemo } from 'react';

import { GapHorizontalIcon } from '@/assets/icons';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { isHighwayArea } from '@/modules/floorplanDesign/helpers';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { HighwayArea } from '@thive/fddm-types';
import { MIN_HIGHWAY_THICKNESS } from '@/modules/floorplanDesign/constants/areas';
import { NumUtils } from '@/modules/common/utils';

interface Props {
  selectedHighway: HighwayArea;
}

export const ThicknessComponent = ({ selectedHighway }: Props) => {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const value = useMemo(() => {
    if (!selectedHighway.segments || selectedHighway.segments.length === 0) return;
    return NumUtils.metersToMmRounded(
      selectedHighway.segments[0].leftSideWidth + selectedHighway.segments[0].rightSideWidth,
    );
  }, [selectedHighway.segments]);

  const onChange = useCallback(
    (newValueMm: number) => {
      if (!isHighwayArea(selectedHighway)) return;

      if (Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);

      fddmSvc.setPreference('highwayArea.segment.width', newValue);
      fddmSvc.updateArea({
        ...selectedHighway,
        segments: minItemsArrayUtils.mapAtLeast1(selectedHighway.segments, (segment) => ({
          ...segment,
          leftSideWidth: newValue / 2,
          rightSideWidth: newValue / 2,
        })),
      });
    },
    [selectedHighway, fddmSvc],
  );

  if (
    selectedHighway.segments === undefined ||
    selectedHighway.segments.length <= 1 ||
    value === undefined
  )
    return null;

  return (
    <PropertiesLabel i18nkey='interface:properties.highway.width'>
      <NumberInput
        value={value}
        unit='mm'
        prefix={<GapHorizontalIcon />}
        sx={{ width: '100%' }}
        minValue={NumUtils.metersToMmRounded(MIN_HIGHWAY_THICKNESS)}
        onChange={onChange}
      />
    </PropertiesLabel>
  );
};

export const Thickness = memo(ThicknessComponent);
